import { styled } from "@mui/material/styles";
import { Link, useLocation, createSearchParams } from "react-router-dom";
import Colors from "../theme/Colors";
import { color } from "chart.js/helpers";

// Construye el `to` de react-router a partir del `path` del sidebar.
// Los paths con "?" navegan a `pathname?param=<query>`; los planos, tal cual.
const toLocation = (path) => {
  if (!path.includes("?")) return path;
  const [pathname, query] = path.split("?");
  return { pathname, search: `?${createSearchParams({ param: query })}` };
};

// Determina si un `path` del sidebar corresponde a la ubicación actual.
// Replica la lógica de navegación de abajo: los paths con "?" navegan a
// `pathname?param=<query>`, los planos navegan tal cual (ignorando cualquier
// query extra como ?current-view dentro de un dashboard).
export const isPathActive = (path, location) => {
  if (!path) return false;
  const [pathname, query] = path.split("?");
  if (location.pathname !== pathname) return false;
  if (query === undefined) return true;
  return new URLSearchParams(location.search).get("param") === query;
};

// Subítem del sidebar como <Link> (anchor real) propio. Se usa un elemento
// directo (no un div) a propósito: la regla global
// `.sidebar div { background-color: #002c54; ... }` de App.scss gana en
// especificidad a las clases de MUI, por eso MenuItem + ListItemText dejaban un
// rectángulo oscuro interno imposible de sobrescribir limpio. Al renderizar el
// texto directo en el elemento, el hover queda parejo.
// Es un <Link> (no un <button>) para que el navegador ofrezca "abrir en nueva
// pestaña/ventana" con click derecho, y para soportar Ctrl/⌘+click y click del
// medio de forma nativa.
const SubItemButton = styled(Link, {
  shouldForwardProp: (prop) => prop !== "active",
})(({ active }) => ({
  display: "block",
  width: "calc(100% - 10px)",
  margin: "2px 5px",
  padding: "8px 14px",
  border: "none",
  textDecoration: "none",
  // Ítem activo (según URL): gradiente de marca persistente. Reposo: transparente.
  background: active
    ? `linear-gradient(195deg, ${Colors.AzulMediline}, ${Colors.AzulMedilineClaro})`
    : "transparent",
  color: "#ffffff",
  fontFamily: "Raleway, Arial",
  fontSize: "13px",
  fontWeight: active ? 700 : 400,
  textAlign: "right",
  borderRadius: "10px",
  cursor: "pointer",
  position: "relative",
  overflow: "hidden",
  transition: "0.2s ease-in-out all",
  "&:hover": {
    backgroundColor: active ? undefined : Colors.AzulMedilineClaro,
    color: "#ffffff",
    textDecoration: "none"
  },
  // Subrayado animado (acento azul) — patrón existente del sidebar.
  // Si está activo queda fijo desplegado; si no, aparece en hover.
  "&::after": {
    content: '""',
    position: "absolute",
    left: 0,
    bottom: 0,
    width: "100%",
    height: "2px",
    backgroundColor: "#49a3f1",
    transform: active ? "scaleX(1)" : "scaleX(0)",
    transformOrigin: active ? "bottom left" : "bottom right",
    transition: "transform 0.25s ease-out",
  },
  "&:hover::after": {
    transform: "scaleX(1)",
    transformOrigin: "bottom left",
  },
}));

const SidebarSubItem = ({ title, path, setIsSidebarOpen }) => {
  const location = useLocation();
  const active = isPathActive(path, location);

  const handleClick = (e) => {
    // Dejar que el navegador maneje ctrl/⌘/shift+click (abrir en nueva
    // pestaña/ventana): no corremos los efectos de navegación in-app.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    window.scrollTo({ top: 0, behavior: "smooth" });
    setIsSidebarOpen(false);
  };

  return (
    <SubItemButton to={toLocation(path)} active={active} onClick={handleClick}>
      {title}
    </SubItemButton>
  );
};

export default SidebarSubItem;
